import { cacheDeserialize, cacheSerialize } from '@/stores'
import { defineStore } from 'pinia'
import { apiGlobalSearchesPost, type EffortJsonldVueGlobalSearchDefaultReadSync, type GlobalSearchJsonldGlobalSearch } from '@/api'
import { useLogger } from '@/composables/useLogger.ts'
import { useStoreCache } from '@/composables/useStoreCache.ts'

export interface SearchResultEffortGroups {
  PARTNERCOLLECTOR?: EffortJsonldVueGlobalSearchDefaultReadSync[]
  ORDER?: EffortJsonldVueGlobalSearchDefaultReadSync[]
  SERVICECALL?: EffortJsonldVueGlobalSearchDefaultReadSync[]
}

export interface SearchResult {
  resources: unknown[]
  results: SearchResultEffortGroups
  _scopes: ('PARTNERCOLLECTOR' | 'ORDER' | 'SERVICECALL')[]
  _search: string
}

export type AllScopesType = keyof Required<GlobalSearchJsonldGlobalSearch>['results']
const storeId = 'global-search-store'
const logger = useLogger(storeId)
export const useGlobalSearchStore = defineStore(storeId, {
  state: () => ({
    $cache: useStoreCache(storeId)
  }),
  persist: {
    pick: ['$cache'],
    serializer: {
      serialize: cacheSerialize,
      deserialize: cacheDeserialize
    }
    /*afterHydrate: (ctx) => {
     ctx.store.$cache_innerTrimCache() //run expire clean directly after loading
     }*/
  },
  actions: {
    getScopes(): {
      key: AllScopesType | ''
      value: string
    }[] {
      const scopes: Record<AllScopesType | '', string> = {
        '': 'Everywhere',
        PARTNERCOLLECTOR: 'Business partners',
        ORDER: 'Orders',
        SERVICECALL: 'Service calls',

        EMPLOYEE: 'Mitarbeiter',
        MODULE: 'Modul',
        INFOBOARD: 'Infoboard',
        EFFORTESTIMATION: 'Anfragenverwaltung',
        REQUEST: 'Auftrag'
      }
      return Object.entries(scopes).map(([key, value]) => ({
        key: key as AllScopesType,
        value: value
      }))
    },

    /**
     *
     * @param search Search string
     * @param scopes Scopes to include (mix of PARTNERCOLLECTOR,ORDER,SERVICECALL or empty string for all)
     * @param includeClosed Also includes blocked entries in result
     */
    async search(search: string, scopes: AllScopesType[] = [], includeClosed = false): Promise<SearchResult> {
      const body = {
        search: search,
        scopes: scopes,
        includeClosed: includeClosed
      }

      const res = await this.$cache.getOrCreate(body, async () => (await apiGlobalSearchesPost<true>({ body: body }).then((d) => d.data)) as SearchResult | undefined, 'collection')

      if (!res.value) throw new Error('Failed to do search')
      /*
       const allResultEfforts = [...(res.value.results.ORDER ?? []), ...(res.value.results.PARTNERCOLLECTOR ?? []), ...(res.value.results.SERVICECALL ?? [])]
       const allPartners = allResultEfforts.flatMap((effort) => effort.partner) as string[]
       void useGeneratedBusinessPartnerStore()
       .fetchAllByIri(allPartners)
       .then((d) => d.value) //will prepopulate cache
       */
      return res.value
    }
  }
})
